import { useMemo, useState, useEffect } from "react";
import { Images } from "../../../../utils/imageConstants";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { formatBackendEnumLabel, formatPlayerRoleLabel, getPlayerImageUrl } from "../../../../helper/helper";
import type { TabTeamsKey } from "../types/teams";
import type { IPlayerProfileResponse, IPlayerStatsApiResponse, IBattingStats, IBowlingStats } from "../../tournaments/tournamentsDetails/matches/matchesDetails/types/playerProfile";

type StatsFormat = "TEST" | "ODI" | "T20" | "T10" | "HUNDRED";

const ALL_FORMATS: StatsFormat[] = ["TEST", "ODI", "T20", "T10", "HUNDRED"];

const hasFormatData = (api: IPlayerStatsApiResponse, format: string): boolean => {
  const formatData = api[format];
  if (!formatData || typeof formatData !== "object") return false;
  const category = Object.values(formatData)[0];
  if (!category || typeof category !== "object") return false;
  return !!(category.battingStats || category.bowlingStats);
};

const getAvailableFormats = (api: IPlayerStatsApiResponse | null | undefined): StatsFormat[] => {
  if (!api || typeof api !== "object") return [];
  return (ALL_FORMATS as StatsFormat[]).filter((fmt) => hasFormatData(api, fmt));
};

const formatStatValue = (v: number | null | undefined): string => (v != null && !Number.isNaN(v) ? String(v) : "-");

/** Batting average, strike rate — always two decimal places (e.g. 13.36) */
const formatDecimal2 = (v: number | null | undefined): string => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : "-");

const battingStatsToItems = (s?: IBattingStats | null): { label: string; value: string }[] => {
  if (!s) return [];
  return [
    { label: "Matches Played", value: formatStatValue(s.totalMatchesCount) },
    { label: "Innings Played", value: formatStatValue(s.totalInningsCount) },
    { label: "Total Runs", value: formatStatValue(s.totalRunsScored) },
    { label: "Batting Avg", value: formatDecimal2(s.average) },
    { label: "Strike Rate", value: formatDecimal2(s.strikeRate) },
    { label: "4's", value: formatStatValue(s.foursCount) },
    { label: "6's", value: formatStatValue(s.sixesCount) },
    { label: "Highest Score", value: formatStatValue(s.highestScore) },
    { label: "Century Count", value: formatStatValue(s.centuriesCount) },
    { label: "Fifty Count", value: formatStatValue(s.halfCenturiesCount) },
  ];
};

const bowlingStatsToItems = (s?: IBowlingStats | null): { label: string; value: string }[] => {
  if (!s) return [];
  const bowlingAvg = s.totalWickets != null && s.totalWickets > 0 && s.totalRunsCenceded != null ? (s.totalRunsCenceded / s.totalWickets).toFixed(2) : "-";
  return [
    { label: "Matches Played", value: formatStatValue(s.totalMatchesCount) },
    { label: "Innings Bowled", value: formatStatValue(s.totalInningsCount) },
    { label: "Wickets Taken", value: formatStatValue(s.totalWickets) },
    { label: "Bowling Avg", value: bowlingAvg },
    { label: "5W", value: formatStatValue(s.fiveWicketsCount) },
    { label: "10W", value: formatStatValue(s.tenWicketsCount) },
    { label: "Economy", value: formatStatValue(s.economy) },
    { label: "Dots", value: "-" },
    { label: "Maiden", value: formatStatValue(s.totalMaidenOverCount) },
  ];
};

const formatDob = (dob?: string): string => {
  if (!dob) return "-";
  try {
    const d = new Date(dob);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return dob;
  }
};

const PlayerProfile = ({ tabToRestore: tabToRestoreDefault = "stats" as TabTeamsKey }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { tabToRestore?: TabTeamsKey; returnTo?: string } | null | undefined;
  const tabToRestore = navState?.tabToRestore ?? tabToRestoreDefault;
  const returnTo = navState?.returnTo;
  const [searchParams] = useSearchParams();
  const [statsFormat, setStatsFormat] = useState<StatsFormat>("TEST");

  const playerId = useMemo(() => {
    const fromQuery = searchParams.get("playerId");
    if (fromQuery) {
      sessionStorage.setItem("playerId", fromQuery);
      return fromQuery;
    }
    return sessionStorage.getItem("playerId") ?? "";
  }, [location.key, searchParams]);

  const { data, isLoading, isError } = useQuery<IPlayerProfileResponse | undefined>({
    queryKey: [QUERY_KEY.getPlayerProfile, playerId],
    enabled: !!playerId,
    queryFn: async () => {
      if (!playerId) return undefined;
      const res = await Api.get(`/getPlayerProfile/${playerId}`);
      return res?.data as IPlayerProfileResponse;
    },
  });

  const player = data?.playerResponse;

  const { data: statsData } = useQuery<IPlayerStatsApiResponse | unknown>({
    queryKey: [QUERY_KEY.getPlayerStats, playerId],
    enabled: !!playerId,
    queryFn: async () => {
      if (!playerId) return null;
      const res = await Api.get(`/playerStats`, {
        params: { playerId },
      });
      return (res?.data ?? null) as IPlayerStatsApiResponse | null;
    },
  });

  const api = statsData as IPlayerStatsApiResponse | null | undefined;
  const availableFormats = useMemo(
    () => getAvailableFormats(api),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is statsData cast, statsData is the source of truth
    [statsData],
  );

  useEffect(() => {
    if (availableFormats.length === 0) return;
    if (!availableFormats.includes(statsFormat)) {
      setStatsFormat(availableFormats[0]);
    }
  }, [availableFormats, statsFormat]);

  const defaultSections = [
    {
      title: "BATTING",
      items: [
        { label: "Matches Played", value: "-" },
        { label: "Innings Played", value: "-" },
        { label: "Total Runs", value: "-" },
        { label: "Batting Avg", value: "-" },
        { label: "Strike Rate", value: "-" },
        { label: "4's", value: "-" },
        { label: "6's", value: "-" },
        { label: "Highest Score", value: "-" },
        { label: "Century Count", value: "-" },
        { label: "Fifty Count", value: "-" },
      ],
    },
    {
      title: "BOWLING",
      items: [
        { label: "Matches Played", value: "-" },
        { label: "Innings Bowled", value: "-" },
        { label: "Wickets Taken", value: "-" },
        { label: "Bowling Avg", value: "-" },
        { label: "5W", value: "-" },
        { label: "10W", value: "-" },
        { label: "Economy", value: "-" },
        { label: "Dots", value: "-" },
        { label: "Maiden", value: "-" },
      ],
    },
    {
      title: "FIELDING",
      items: [
        { label: "Catches Taken", value: "-" },
        { label: "Stumpings", value: "-" },
        { label: "Run Outs", value: "-" },
      ],
    },
  ];

  const sections = (() => {
    if (!api || typeof api !== "object") return defaultSections;

    let formatData = api[statsFormat];
    if (!formatData || typeof formatData !== "object") {
      formatData = Object.values(api)[0];
    }
    if (!formatData || typeof formatData !== "object") return defaultSections;

    const category = Object.values(formatData)[0];
    if (!category || typeof category !== "object") return defaultSections;

    const battingItems = battingStatsToItems(category.battingStats);
    const bowlingItems = bowlingStatsToItems(category.bowlingStats);

    return [
      {
        title: "BATTING",
        items: battingItems.length > 0 ? battingItems : defaultSections[0].items,
      },
      {
        title: "BOWLING",
        items: bowlingItems.length > 0 ? bowlingItems : defaultSections[1].items,
      },
      defaultSections[2],
    ];
  })();

  const goBack = () => {
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    navigate("/tournaments/team-info", {
      state: { lastTeamsTab: tabToRestore },
      replace: true,
    });
  };

  const playerImage = getPlayerImageUrl(player?.imageUri) ?? Images.player_image;
  const firstName = player?.playerFirstName ?? "";
  const lastName = player?.playerLastName ?? "";
  // const country = (player?.country ?? "").toUpperCase() || "-";
  const dob = formatDob(player?.dob);
  const role = formatPlayerRoleLabel(player?.playerRole ?? "-");
  const battingStyle = formatBackendEnumLabel(player?.battingStyle ?? "-");
  const bowlingStyle = formatBackendEnumLabel(player?.bowlingStyle ?? "-");

  if (!playerId) {
    return (
      <div className="profile-wrapper-container">
        <div className="profile-wrap">
          <div className="back-button">
            <img src={Images.previous_icon} alt="back" onClick={goBack} />
          </div>
          <div className="profile-no-player">
            <p>No player selected. Search for a player to view their profile.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="profile-wrapper-container">
        <div className="profile-wrap">
          <div className="back-button">
            <img src={Images.previous_icon} alt="back" onClick={goBack} />
          </div>
          <div className="profile-loading">
            <p>Loading player profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !player) {
    return (
      <div className="profile-wrapper-container">
        <div className="profile-wrap">
          <div className="back-button">
            <img src={Images.previous_icon} alt="back" onClick={goBack} />
          </div>
          <div className="profile-error">
            <p>Unable to load player profile. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-wrapper-container">
      <div className="profile-wrap">
        <div className="back-button">
          <img src={Images.previous_icon} alt="backButton" onClick={() => goBack()} />
        </div>
        <div className="background-image-wrap">
          <div className="banner-data">
            <div className="image-wrap">
              <img className="hero__avatar" src={playerImage} alt="Player avatar" height={180} width={180} />
            </div>
            <div className="top-data">
              <div className="profile-data">
                <span className="main-name">{firstName}</span>
                <span className="last-name"> {lastName}</span>
                {/* <div className="country-data">
        <div className="flag">
            <img src={Images.country_flag} alt="" />
        </div>
        <div className="country-name">
          <p>{country}</p>
        </div>
       </div> */}
                <div className="basic-details">
                  <div className="label-value">
                    <div className="label">Date of Birth :</div>
                    <div className="value">{dob}</div>
                  </div>
                  <div className="label-value">
                    <div className="label">Role :</div>
                    <div className="value">{role}</div>
                  </div>
                  <div className="label-value">
                    <div className="label">Batting-Style :</div>
                    <div className="value">{battingStyle}</div>
                  </div>
                  <div className="label-value">
                    <div className="label">Bowling-Style :</div>
                    <div className="value">{bowlingStyle}</div>
                  </div>
                </div>
              </div>
              <div className="compare">{/* <button className="compare-button"><img src={Images.compare_button_img} alt="compare-icon"/><span className="">Compare</span></button> */}</div>
            </div>
          </div>
        </div>
        <div className="after-main-container">
          {availableFormats.length > 0 && (
            <div className="tab-wrapper">
              {availableFormats.map((fmt) => (
                <button key={fmt} type="button" className={`format-tab ${statsFormat === fmt ? "active" : ""}`} onClick={() => setStatsFormat(fmt)}>
                  {fmt}
                </button>
              ))}
            </div>
          )}
          <div className="tables-wrapper">
            {sections.map((sec) => (
              <section className="stat-card" key={sec.title} aria-label={sec.title}>
                <header className="card-head">
                  <div className="head-left">
                    <h3 className="title">{sec.title}</h3>
                  </div>
                </header>

                <div className="card-body">
                  {sec.items.map((row, idx) => (
                    <div className={`stat-row${idx === sec.items.length - 1 ? " last" : ""}`} key={`${sec.title}-${row.label}`}>
                      <span className="label">{row.label}</span>
                      <span className="value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
